import React, { useState } from "react";
import { FiX, FiPlus, FiSend } from "react-icons/fi";

/**
 * Raise a new request.
 *
 * Deliberately separate from the Roadmap Progress modal: asking for something
 * new is its own action, not a way of reading where existing work stands.
 * Bundling the two meant the form was only reachable by opening a dashboard
 * that had nothing to do with it.
 *
 * Submitting emails the roadmap owner and records the request as PENDING
 * REVIEW. It deliberately does not appear on the roadmap: showing it under
 * "What's coming up" straight away implied the work had been accepted, when
 * it still has to be reviewed.
 *
 * The owner is named on the form itself, before submitting rather than only
 * in the confirmation — "who is going to read this" is the first thing anyone
 * filling it in wants to know.
 */
// Matches NVARCHAR(1000) on mn.ReleaseBacklog.Title and the API's own cap.
// If these three ever disagree the longest one wins silently at the database,
// which reads as lost text rather than a limit.
const MAX_TITLE_LENGTH = 1000;

const RequestFeatureModal = ({ open, onClose, onCreate, owner = null }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [eta, setEta] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  if (!open) return null;

  const reset = () => {
    setTitle("");
    setDescription("");
    setEta("");
    setError("");
    setDone(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await onCreate({
        title: trimmed,
        description: description.trim(),
        eta: eta.trim(),
      });
      // Confirm rather than closing straight away — otherwise there's no sign
      // the request actually went anywhere.
      setDone(true);
      setTitle("");
      setDescription("");
      setEta("");
    } catch (err) {
      setError(err?.message || "Couldn't submit the request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rn-modal-overlay" onClick={close}>
      <div
        className="rn-modal rn-modal--narrow"
        role="dialog"
        aria-modal="true"
        aria-label="New request"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rn-modal-head">
          <div className="rn-modal-title">
            <FiPlus size={18} />
            <div>
              <h2>New Request</h2>
              <p>Tell us what would make the product work better for you.</p>
            </div>
          </div>
          <button
            type="button"
            className="rn-modal-close"
            onClick={close}
            aria-label="Close"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="rn-modal-body">
          {done ? (
            <div className="rn-request-done">
              <p className="rn-request-done-title">Thanks — that's with us.</p>
              <p>
                Your request has been emailed to{" "}
                <strong>{owner?.name || "the roadmap owner"}</strong> for
                review. It isn't on the roadmap yet — if it's taken forward,
                you'll see it appear under <strong>Upcoming</strong>.
              </p>
              <div className="rn-request-done-actions">
                <button
                  type="button"
                  className="rn-request-submit"
                  onClick={() => setDone(false)}
                >
                  Request another
                </button>
                <button
                  type="button"
                  className="rn-admin-cancel"
                  onClick={close}
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            <form className="rn-request-form is-standalone" onSubmit={handleSubmit}>
              {/* Stated before the form, not after submitting: where this
                  goes and who decides is the first question anyone has. */}
              {owner?.name ? (
                <div className="rn-owner-note">
                  <span>
                    This goes to <strong>{owner.name}</strong>, who reviews
                    every request and decides what joins the roadmap. Submitting
                    doesn't add it to the roadmap.
                  </span>
                </div>
              ) : null}

              <label className="rn-request-label" htmlFor="rn-req-title">
                What would you like us to build?
              </label>
              <input
                id="rn-req-title"
                className="rn-request-input"
                type="text"
                placeholder="A short summary of the request"
                value={title}
                maxLength={MAX_TITLE_LENGTH}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus
              />
              {/* The cap existed before but was invisible: typing simply
                  stopped with no explanation. The count is what makes it
                  legible, so it renders from the first character rather than
                  appearing only once the limit is close. */}
              <div
                className={`rn-char-count ${
                  title.length >= MAX_TITLE_LENGTH ? "is-max" : ""
                }`}
                aria-live="polite"
              >
                {title.length}/{MAX_TITLE_LENGTH}
              </div>

              <label className="rn-request-label" htmlFor="rn-req-desc">
                Any context? <span className="rn-optional">(optional)</span>
              </label>
              <textarea
                id="rn-req-desc"
                className="rn-request-textarea"
                placeholder="The problem you're trying to solve, who it affects, anything that would help us understand it"
                value={description}
                rows={4}
                onChange={(e) => setDescription(e.target.value)}
              />

              <label className="rn-request-label" htmlFor="rn-req-eta">
                Desired timing <span className="rn-optional">(optional)</span>
              </label>
              <input
                id="rn-req-eta"
                className="rn-request-eta is-standalone"
                type="text"
                placeholder="e.g. before the end of Q3"
                value={eta}
                maxLength={100}
                onChange={(e) => setEta(e.target.value)}
              />

              <div className="rn-request-actions is-standalone">
                <button
                  type="button"
                  className="rn-admin-cancel"
                  onClick={close}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rn-request-submit"
                  disabled={!title.trim() || submitting}
                >
                  <FiSend size={13} />
                  {submitting ? "Submitting…" : "Submit request"}
                </button>
              </div>

              {error ? <div className="rn-request-error">{error}</div> : null}
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestFeatureModal;
